import { nanoid } from 'nanoid';
import { UseFormSetError } from 'react-hook-form';
import { REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES } from 'src/components/Reports/ReportsTableEditor/ReportsTableEditorForm.constants';
import { COMMISSION_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/commission/commissionColumnUtils.types';
import { COST_OF_GOODS_SOLD_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/costOfGoodsSold/costOfGoodsSoldColumnUtils.types';
import { LISTING_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { PURCHASE_ORDER_LINE_ITEM_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/purchaseOrderLineItem/purchaseOrderLineItemColumnUtils.types';
import { PURCHASE_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/purchases/purchasesReportColumnUtils.types';
import {
  expressionToTokens,
  getCustomColumnDisplayName,
  getCustomColumnId,
  isCustomColumnName,
  validateTokensSyntax,
} from 'src/utils/columns/reportV2CustomColumnUtils';
import { SALE_COST_ADJUSTMENTS_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/saleCostAdjustments/saleCostAdjustmentsColumnUtils.types';
import { SALE_LINE_ITEM_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/saleLineItem/saleLineItemColumnUtils.types';
import { SALES_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/sales/salesColumnUtils.types';
import { SOLD_TICKET_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/soldTicket/soldTicketColumnUtils.types';
import { TICKET_GROUP_REPORT_TABLE_FORMULA_COLUMNS } from 'src/utils/columns/ticketGroup/ticketGroupColumnUtils.types';
import { ReportTypesV2 } from 'src/utils/reportsUtils';
import { ReportColumn } from 'src/WebApiController';

import { CustomColumnType } from './TableCustomColumnModal.types';

export const REPORT_TYPE_TO_FORMULA_COLUMNS = {
  [ReportTypesV2.InventoryV2]: LISTING_REPORT_TABLE_FORMULA_COLUMNS,
  [ReportTypesV2.SaleV2]: SALES_REPORT_TABLE_FORMULA_COLUMNS,
  [ReportTypesV2.Purchase]: PURCHASE_REPORT_TABLE_FORMULA_COLUMNS,
  [ReportTypesV2.SaleLineItem]: SALE_LINE_ITEM_REPORT_TABLE_FORMULA_COLUMNS,
  [ReportTypesV2.PurchaseOrderLineItem]:
    PURCHASE_ORDER_LINE_ITEM_REPORT_TABLE_FORMULA_COLUMNS,
  [ReportTypesV2.SoldTicket]: SOLD_TICKET_REPORT_TABLE_FORMULA_COLUMNS,
  [ReportTypesV2.Commission]: COMMISSION_REPORT_TABLE_FORMULA_COLUMNS,
  [ReportTypesV2.CostOfGoodsSold]:
    COST_OF_GOODS_SOLD_REPORT_TABLE_FORMULA_COLUMNS,
  [ReportTypesV2.SaleCostAdjustments]:
    SALE_COST_ADJUSTMENTS_REPORT_TABLE_FORMULA_COLUMNS,
  [ReportTypesV2.TicketGroup]: TICKET_GROUP_REPORT_TABLE_FORMULA_COLUMNS,
};

export const getDetaultValueForColumnInput = (
  columnName: string | undefined,
  columnValue: string | undefined
): CustomColumnType => {
  let columnId = '';
  let name = '';
  const formula = columnValue ?? '';
  if (isCustomColumnName(columnName ?? '')) {
    name = getCustomColumnDisplayName(columnName!);
    columnId = getCustomColumnId(columnName!);
  } else {
    columnId = nanoid().replace('_', '-');
  }

  return {
    id: columnId,
    name: name,
    formula: formula,
    dependency: [], // not used in reporting
  };
};

export const getPersonalizationConfigFromFormulaFieldId = (
  reportType: ReportTypesV2,
  field: string
) => {
  const overrideMap =
    REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES[reportType];
  return overrideMap[field as keyof typeof overrideMap];
};

export function validateNewCustomColumn(
  name: string,
  formula: string,
  fieldOptionsPreset: string[],
  presetColumnNames: string[],
  existingCustomColumns: ReportColumn[],
  columnToEditId: string | undefined,
  setError: UseFormSetError<CustomColumnType>,
  columnWithSameNameExistsMsg: string,
  invalidFormulaMsg: string,
  requiredMsg: string
) {
  let hasError = false;

  // check empty
  if (!name) {
    setError('name', { message: requiredMsg }, { shouldFocus: true });
    hasError = true;
  }

  if (!formula) {
    setError('formula', { message: requiredMsg }, { shouldFocus: true });
    hasError = true;
  }

  // check against not only custom column but predefined columns
  const allColumnNames = [
    ...existingCustomColumns
      .filter(
        (c) =>
          isCustomColumnName(c.columnName) &&
          getCustomColumnId(c.columnName) !== columnToEditId
      )
      .map((c) => getCustomColumnDisplayName(c.columnName)),
    ...presetColumnNames,
  ];

  if (
    allColumnNames.some(
      (n) => n.toLocaleLowerCase() === name.toLocaleLowerCase()
    )
  ) {
    setError(
      'name',
      { message: columnWithSameNameExistsMsg },
      { shouldFocus: true }
    );
    hasError = true;
  }

  if (
    !validateTokensSyntax(
      expressionToTokens(formula, fieldOptionsPreset),
      fieldOptionsPreset
    )
  ) {
    setError('formula', { message: invalidFormulaMsg }, { shouldFocus: true });
    hasError = true;
  }

  return !hasError;
}
